import { Box, Typography, Paper, IconButton, useTheme } from "@mui/material";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { FiDownload, FiX } from "react-icons/fi";
import { LuFactory, LuTruck, LuZap, LuLayers, LuLeaf } from "react-icons/lu";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Cell
} from "recharts";

import { MainContainer } from "../components/container/MainContainer";
import MetricCard from "../app_components/MetricCard";
import AgGrid from "../components/grid/AgGrid";
import {
  defaultColDef, leftAlignColDef, rightAlignColDef, floatFormater,
} from "../components/grid/columnDefinitions/columnDefinitions";
import GeneratePPTButton from "../components/button/GeneratePPTButton";
import { GRAPH_PALETTE } from "../theme/style/graphChartPalette";
import { getOrgTokens, ORG_PALETTE } from "../theme/style/organizationPalette";
import "../style/pages-style/Comparison.css";

const SCOPE_COLORS = ["#659d33", "#38bdf8", "#fbbf24", "#a78bfa", "#f87171", "#34d399", "#fb923c"];

const scopeMetaMap = [
  { value: "scope_1", label: "Scope 1", icon: LuFactory, accent: "#fbbf24", bg: "rgba(251,191,36,0.10)", type: "scope1" },
  { value: "scope_2", label: "Scope 2", icon: LuZap,     accent: "#4ade80", bg: "rgba(34,197,94,0.10)",  type: "scope2" },
  { value: "scope_3", label: "Scope 3", icon: LuTruck,   accent: "#38bdf8", bg: "rgba(14,165,233,0.10)", type: "scope3" },
  { value: "scope_4", label: "Scope 4", icon: LuLayers,  accent: "#a78bfa", bg: "rgba(168,85,247,0.10)", type: "shield" },
  { value: "total",   label: "Total",   icon: LuLeaf,    accent: "#16a34a", bg: "rgba(22,163,74,0.12)",  type: "gwp"    },
];

function OrganizationalComparison({ Org }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const [comparisonBase, setComparisonBase] = useState(null);
  const [comparisonTo, setComparisonTo] = useState([]);
  const [comparisonBasedOnOptions, setComparisonBasedOnOptions] = useState([]);
  const [rawComparisonData, setRawComparisonData] = useState([]);

  const scopes = useMemo(() => scopeMetaMap, []);

  const percentageDifference = (value1, value2) => {
    if (value1 === 0 || value2 === 0) return 0;
    const denominator = (value1 + value2) / 2;
    return denominator !== 0 ? (((value1 - value2) / denominator) * 100).toFixed(2) : 0;
  };

  useEffect(() => {
    Org.get_mine()
      .then((r) => r.json())
      .then((data) => {
        setComparisonBasedOnOptions(
          data.map((i) => ({ value: i.id, label: i.name })).sort((a, b) => a.label.localeCompare(b.label))
        );
      });
  }, [Org]);

  const sortedData = useMemo(() => {
    return [...rawComparisonData]
      .map((d) => {
        const opt = comparisonBasedOnOptions.find((o) => o.value === d.id);
        const computedTotal =
          (parseFloat(d.scope_1) || 0) +
          (parseFloat(d.scope_2) || 0) +
          (parseFloat(d.scope_3) || 0) +
          (parseFloat(d.scope_4) || 0);
        const total = parseFloat(d.total) > 0 ? parseFloat(d.total) : computedTotal;
        return { ...d, name: opt ? opt.label : (d.name ?? String(d.id)), total };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rawComparisonData, comparisonBasedOnOptions]);

  const selectedData = useMemo(
    () => sortedData.find((item) => item?.id === comparisonBase),
    [sortedData, comparisonBase]
  );
  const selectedComparisonData = useMemo(() => selectedData || {}, [selectedData]);

  const percentageDifferences = useMemo(() => {
    const ret = {};
    sortedData.forEach((item) => {
      if (item.id !== selectedData?.id) {
        ret[item.id] = percentageDifference(parseFloat(item.total), parseFloat(selectedData?.total || 0));
      }
    });
    return ret;
  }, [sortedData, selectedData]);

  // ── AgGrid column defs ─────────────────────────────────────────────
  const colDefs = useMemo(() => [
    {
      headerName: "Name", field: "name",
      ...defaultColDef, ...leftAlignColDef,
      minWidth: 200, flex: 2,
      cellStyle: { color: "#659d33", fontWeight: 600, fontFamily: "Poppins", textAlign: "left", justifyContent: "flex-start", paddingLeft: "20px" },
    },
    { headerName: "Scope 1", field: "scope_1", ...defaultColDef, ...rightAlignColDef, ...floatFormater },
    { headerName: "Scope 2", field: "scope_2", ...defaultColDef, ...rightAlignColDef, ...floatFormater },
    { headerName: "Scope 3", field: "scope_3", ...defaultColDef, ...rightAlignColDef, ...floatFormater },
    { headerName: "Scope 4", field: "scope_4", ...defaultColDef, ...rightAlignColDef, ...floatFormater },
    {
      headerName: "Total", field: "total",
      ...defaultColDef, ...rightAlignColDef, ...floatFormater,
      cellStyle: { fontWeight: 700, fontFamily: "Poppins", textAlign: "right", justifyContent: "flex-end", paddingRight: "20px" },
    },
  ], []);

  // ── Emission grouped bar data (X = scope, bars = orgs) ────────────
  const activeOrgs = useMemo(
    () => comparisonBasedOnOptions.filter((o) => comparisonTo.includes(o.value) || o.value === comparisonBase),
    [comparisonBasedOnOptions, comparisonTo, comparisonBase]
  );

  const emissionBarData = useMemo(() => {
    return scopes.map((scope) => {
      const row = { label: scope.label };
      sortedData.forEach((item) => { row[item.id] = parseFloat(item[scope.value]) || 0; });
      return row;
    });
  }, [sortedData, scopes]);

  const percentageDifferenceDataKeys = useMemo(() => [{ key: "value", label: "Percentage Difference" }], []);

  // ── Percentage difference bar data ────────────────────────────────
  const percentageDifferenceData = useMemo(
    () =>
      Object.entries(percentageDifferences).map(([id, value]) => {
        const option = comparisonBasedOnOptions.find((o) => o.value === id);
        return { label: option ? option.label : id, value: Number(value) };
      }),
    [percentageDifferences, comparisonBasedOnOptions]
  );

  useEffect(() => {
    if (!comparisonBase) setComparisonTo([]);
    else setComparisonTo((prev) => prev.filter((p) => p !== comparisonBase));
  }, [comparisonBase]);

  useEffect(() => {
    if (!comparisonBase || comparisonTo.length < 1) return;
    const allIds = [...new Set([...comparisonTo, comparisonBase])];
    Org.get_total_gwp_by_scope(allIds)
      .then((r) => r.json())
      .then((data) => setRawComparisonData(data));
  }, [comparisonBase, comparisonTo, Org]);

  // ── Shared select styles ──────────────────────────────────────────
  const selectStyles = useMemo(() => ({
    control: (base, state) => ({
      ...base,
      borderRadius: "8px",
      border: state.isFocused ? "1px solid #659d33" : `1px solid ${tok.border.card}`,
      minHeight: "44px",
      backgroundColor: isDark ? "#18181b" : "white",
      boxShadow: "none",
      "&:hover": { borderColor: "#659d33" },
    }),
    menu: (base) => ({ ...base, backgroundColor: isDark ? "#18181b" : "white", zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#659d33"
        : state.isFocused
        ? (isDark ? "rgba(101,157,51,0.15)" : "rgba(101,157,51,0.08)")
        : "transparent",
      color: state.isSelected ? "white" : (isDark ? "white" : "black"),
      "&:active": { backgroundColor: "#659d33" },
    }),
    singleValue: (base) => ({ ...base, color: isDark ? "white" : "black" }),
    multiValue: (base) => ({ ...base, backgroundColor: isDark ? "rgba(101,157,51,0.2)" : "rgba(101,157,51,0.1)", borderRadius: "6px" }),
    multiValueLabel: (base) => ({ ...base, color: isDark ? "#a3e635" : "#3a6e10", fontWeight: 600 }),
    multiValueRemove: (base) => ({ ...base, "&:hover": { backgroundColor: "#ef4444", color: "white" } }),
    input: (base) => ({ ...base, color: isDark ? "white" : "black" }),
    placeholder: (base) => ({ ...base, color: isDark ? "#a1a1aa" : "#64748b" }),
  }), [isDark, tok]);

  // ── Axis / tooltip style helpers ──────────────────────────────────
  const axisProps = {
    tick: { fill: tok.text.chartTitle, fontSize: GRAPH_PALETTE.font.sizes.standard, fontFamily: GRAPH_PALETTE.font.family, fontWeight: GRAPH_PALETTE.font.weights.standard },
    axisLine: { stroke: isDark ? GRAPH_PALETTE.axes?.darkMode?.line ?? "#374151" : GRAPH_PALETTE.axes?.lightMode?.line ?? "#e5e7eb" },
    tickLine: { stroke: isDark ? "#374151" : "#e5e7eb" },
  };
  const tooltipStyle = {
    contentStyle: {
      backgroundColor: tok.bg.card, border: `1px solid ${tok.border.card}`,
      borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.standard,
    },
    itemStyle: { fontSize: GRAPH_PALETTE.font.sizes.standard },
    cursor: { fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)" },
  };
  const legendStyle = {
    verticalAlign: "top", align: "right", iconType: "circle",
    wrapperStyle: { paddingBottom: 24, fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.standard, fontWeight: GRAPH_PALETTE.font.weights.medium },
  };

  return (
    <MainContainer>

      {/* ── Metric Cards: Scope 1 / 2 / 3 / 4 / Total ─────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: ORG_PALETTE.spacing.metricCardGap, mb: 4 }}>
        {scopeMetaMap.map((s, i) => (
          <Box key={s.value} sx={{ minWidth: 0, "& .eng-metric-card__value": { whiteSpace: "normal !important", flexWrap: "wrap" } }}>
            <MetricCard
              title={t(s.label)}
              value={parseFloat(selectedComparisonData[s.value]) || 0}
              unit="kg CO₂e"
              icon={s.icon}
              accent={s.accent}
              bg={isDark ? ORG_PALETTE.metricCards[s.type]?.bgDark : s.bg}
              type={s.type}
              delay={i * 0.08}
              variant="default"
            />
          </Box>
        ))}
      </Box>

      {/* ── Selection Section ──────────────────────────────────────── */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography sx={{ fontFamily: "Poppins", fontSize: "20px", fontWeight: 500, color: isDark ? "#fff" : "#0f172a", textAlign: "left" }}>
            {t("Comparison Selection")}
          </Typography>
          <GeneratePPTButton onClick={() => {}} label="Generate Report" />
        </Box>
        <Box
          sx={{
            display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3,
            p: 3, borderRadius: "12px",
            bgcolor: tok.bg.card, border: "1px solid", borderColor: tok.border.card,
            boxShadow: tok.shadow.card, position: "relative", zIndex: 10,
          }}
        >
          {/* Baseline */}
          <Box>
            <Typography variant="caption" sx={{ display: "block", mb: 1, fontWeight: 600, color: tok.text.caption, ml: 0.5 }}>
              {t("Baseline")}
            </Typography>
            <Select
              options={comparisonBasedOnOptions}
              value={comparisonBasedOnOptions.find((o) => o.value === comparisonBase) ?? null}
              onChange={(o) => setComparisonBase(o ? o.value : null)}
              placeholder="Select baseline..."
              isClearable
              menuPortalTarget={document.body}
              styles={selectStyles}
            />
          </Box>

          {/* Compare With */}
          <Box>
            <Typography variant="caption" sx={{ display: "block", mb: 1, fontWeight: 600, color: tok.text.caption, ml: 0.5 }}>
              {t("Compare With")}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", minHeight: "44px" }}>
              {comparisonTo.map((val) => {
                const opt = comparisonBasedOnOptions.find((o) => o.value === val);
                if (!opt) return null;
                return (
                  <Box
                    key={val}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1,
                      pl: 2, pr: 1.5, py: 0.8, borderRadius: "8px",
                      border: "1px solid", borderColor: tok.border.card,
                      bgcolor: isDark ? "rgba(255,255,255,0.05)" : "white",
                      fontSize: "0.85rem", color: tok.text.primary,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      transition: "all 0.2s ease",
                      "&:hover": { borderColor: "#659d33", boxShadow: "0 2px 8px rgba(101,157,51,0.15)" },
                    }}
                  >
                    {opt.label}
                    <Box
                      component="span"
                      sx={{ cursor: "pointer", display: "flex", color: "text.secondary", transition: "color 0.2s", "&:hover": { color: "#ef4444" } }}
                      onClick={() => setComparisonTo((prev) => prev.filter((v) => v !== val))}
                    >
                      <FiX size={13} />
                    </Box>
                  </Box>
                );
              })}
              <Box sx={{ width: comparisonTo.length > 0 ? "180px" : "100%" }}>
                <Select
                  options={comparisonBasedOnOptions.filter((o) => o.value !== comparisonBase && !comparisonTo.includes(o.value))}
                  onChange={(o) => o && setComparisonTo((prev) => [...prev, o.value])}
                  placeholder={comparisonTo.length > 0 ? "Add more..." : "Search and add to compare..."}
                  value={null}
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Footprint Summary Table ────────────────────────────────── */}
      <Paper variant="Table Container" sx={{ mb: 4 }}>
          <Box
            sx={{
              p: 2, px: 3,
              borderBottom: `1px solid ${tok.border.divider}`,
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: tok.text.chartTitle, fontFamily: GRAPH_PALETTE.font.family }}>
                {t("Footprint Summary")}
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: "0.75rem", fontStyle: "italic", display: "flex", alignItems: "center", pt: 0.3 }}>
                ({t("All units are calculated in kg CO₂e")})
              </Typography>
            </Box>
            <IconButton size="small"><FiDownload size={18} /></IconButton>
          </Box>
          <Box sx={{ borderBottom: `1px solid ${tok.border.divider}` }}>
            <AgGrid
              rowData={sortedData}
              columnDefs={colDefs}
              domLayout={sortedData.length > 20 ? "normal" : "autoHeight"}
              pagination={sortedData.length > 20}
            />
          </Box>
        </Paper>

      {/* ── Charts ────────────────────────────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 4, mb: 4 }}>

          {/* Environmental Emission */}
          <Paper elevation={0} sx={{ p: 1, borderRadius: tok.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card, display: "flex", flexDirection: "column" }}>
            <Box sx={{ p: 2, px: 3, borderBottom: `1px solid ${tok.border.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: tok.text.chartTitle, fontFamily: GRAPH_PALETTE.font.family }}>
                {t("Environmental Emission")}
              </Typography>
              <IconButton size="small"><FiDownload size={18} /></IconButton>
            </Box>
            <Box sx={{ p: 3, minHeight: 380 }}>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={emissionBarData} margin={{ top: 10, right: 20, left: 50, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" dy={8} {...axisProps} />
                  <YAxis
                    {...axisProps}
                    label={{ value: "GWP [kg CO₂e]", angle: -90, position: "insideLeft", offset: -30, style: { textAnchor: "middle", fill: tok.text.chartTitle, opacity: 0.8, fontSize: GRAPH_PALETTE.font.sizes.mainAxisTitle, fontFamily: GRAPH_PALETTE.font.family, fontWeight: GRAPH_PALETTE.font.weights.bold } }}
                  />
                  <Tooltip {...tooltipStyle} formatter={(v) => [Number(v).toLocaleString("en-CA", { maximumFractionDigits: 2 }), ""]} />
                  <Legend {...legendStyle} />
                  {activeOrgs.map((org, i) => (
                    <Bar key={org.value} dataKey={org.value} name={org.label} fill={SCOPE_COLORS[i % SCOPE_COLORS.length]} radius={[4, 4, 0, 0]} barSize={20} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Percentage Difference */}
          <Paper elevation={0} sx={{ p: 1, borderRadius: tok.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card, display: "flex", flexDirection: "column" }}>
            <Box sx={{ p: 2, px: 3, borderBottom: `1px solid ${tok.border.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: tok.text.chartTitle, fontFamily: GRAPH_PALETTE.font.family }}>
                {t("Percentage Difference")}
              </Typography>
              <IconButton size="small"><FiDownload size={18} /></IconButton>
            </Box>
            <Box sx={{ p: 3, minHeight: 380 }}>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={percentageDifferenceData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" dy={8} {...axisProps} />
                  <YAxis
                    {...axisProps}
                    label={{ value: "%", angle: -90, position: "insideLeft", style: { textAnchor: "middle", fill: tok.text.chartTitle, opacity: 0.8, fontSize: GRAPH_PALETTE.font.sizes.mainAxisTitle, fontFamily: GRAPH_PALETTE.font.family, fontWeight: GRAPH_PALETTE.font.weights.bold } }}
                  />
                  <Tooltip {...tooltipStyle} formatter={(v) => [`${Number(v).toFixed(2)}%`, "Difference"]} />
                  <ReferenceLine y={0} stroke={isDark ? "#52525b" : "#cbd5e1"} strokeWidth={1.5} />
                  <Bar dataKey="value" name="% Difference" radius={[4, 4, 0, 0]} barSize={28}>
                    {percentageDifferenceData.map((entry, i) => (
                      <Cell key={`cell-${i}`} fill={Number(entry.value) <= 0 ? "#16a34a" : "#ef4444"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

        </Box>

    </MainContainer>
  );
}

export default OrganizationalComparison;
